import { DatePipe } from '@angular/common'
import { Component } from '@angular/core'

import { BaseComponent } from '../../shared'

interface DamagedUtilityDashboardKpi {
    totalDamages: number
    damagesThisMonth: number
    damagesLast30Days: number
    damagesYearToDate: number
    vanConAtFaultCount: number
    vanConAtFaultRate: number
    damagedBeforeExcavationCount: number
    outsideToleranceCount: number
    markedIncorrectlyCount: number
    prePhotosNotTakenCount: number
}

interface MonthlyCount {
    label: string
    count: number
}

interface LabelCount {
    label: string
    count: number
}

interface JobCount {
    jobId: string
    jobName: string
    count: number
}

interface NotificationCompliance {
    total: number
    safetyNotifiedCount: number
    safetyNotifiedRate: number
    pmNotifiedCount: number
    peNotifiedCount: number
    fireAndEmsNotifiedCount: number
}

interface RecentItem {
    id: string
    jobId: string
    jobName: string
    damageDate: string | null
    typeOfUtility: string
    vanConAtFault: string
}

interface DamagedUtilityDashboardMetrics {
    kpi: DamagedUtilityDashboardKpi
    monthlyCounts: MonthlyCount[]
    byUtilityType: LabelCount[]
    byUtilityOwner: LabelCount[]
    topJobs: JobCount[]
    notificationCompliance: NotificationCompliance
    recentDamages: RecentItem[]
}

const emptyMetrics = (): DamagedUtilityDashboardMetrics => ({
    kpi: {
        totalDamages: 0,
        damagesThisMonth: 0,
        damagesLast30Days: 0,
        damagesYearToDate: 0,
        vanConAtFaultCount: 0,
        vanConAtFaultRate: 0,
        damagedBeforeExcavationCount: 0,
        outsideToleranceCount: 0,
        markedIncorrectlyCount: 0,
        prePhotosNotTakenCount: 0,
    },
    monthlyCounts: [],
    byUtilityType: [],
    byUtilityOwner: [],
    topJobs: [],
    notificationCompliance: {
        total: 0,
        safetyNotifiedCount: 0,
        safetyNotifiedRate: 0,
        pmNotifiedCount: 0,
        peNotifiedCount: 0,
        fireAndEmsNotifiedCount: 0,
    },
    recentDamages: [],
})

@Component({
    selector: 'app-damaged-utility-dashboard',
    templateUrl: './damaged-utility-dashboard.component.html',
    styleUrls: ['./damaged-utility-dashboard.component.css'],
    standalone: true,
    imports: [DatePipe],
})
export class DamagedUtilityDashboardComponent extends BaseComponent {
    metrics: DamagedUtilityDashboardMetrics = emptyMetrics()
    metricsLoadError = false

    constructor() {
        super()
        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            'damagedUtilityDashboard',
            true,
            this.services.data.get<DamagedUtilityDashboardMetrics>('/api/DamagedUtility/GetDashboardMetrics').subscribe({
                next: result => {
                    this.metrics = result ?? emptyMetrics()
                    this.metricsLoadError = false
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.metrics = emptyMetrics()
                    this.metricsLoadError = true
                    this.initialLoadingComplete()
                },
            }),
        )
    }

    maxMonthlyCount(): number {
        return Math.max(...this.metrics.monthlyCounts.map(m => m.count), 1)
    }

    maxLabelCount(items: LabelCount[]): number {
        return Math.max(...items.map(i => i.count), 1)
    }
}
